import { Link, useParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { actors } from "@/mocks/actors";
import { episodes } from "@/mocks/episodes";
import { plots } from "@/mocks/plots";

export default function ActorDetail() {
  const { id } = useParams<{ id: string }>();
  const actor = actors.find((item) => item.id === id);

  if (!actor) {
    return (
      <div className="mx-auto max-w-site px-4 py-20 md:px-6">
        <EmptyState
          title="不存在此演员"
          description="你访问的演员资料不存在或链接已失效，请返回演员列表重新选择。"
          secondaryLabel="返回演员列表"
          secondaryLink="/category/actors"
        />
      </div>
    );
  }

  const actorEpisodes = episodes.filter((item) => actor.episodeIds.includes(item.id));
  const relatedArticles = plots.filter((plot) => actor.articleIds.includes(plot.id));

  return (
    <>
      <PageMeta
        title={`${actor.name} 饰 ${actor.role} - 年轻的母亲1演员 - 年轻的母亲1官网`}
        description={`《年轻的母亲1》演员${actor.name}介绍，在剧中饰演${actor.role}。${actor.shortBio}`}
        keywords={`年轻的母亲1 ${actor.name},${actor.name} 饰演 ${actor.role},年轻的母亲1演员介绍`}
      />

      <div className="mx-auto max-w-site px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "演员", to: "/category/actors" }, { label: actor.name }]} />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[320px_minmax(0,1fr)]">
          <div>
            <div className="w-full aspect-square overflow-hidden rounded-lg bg-background-200">
              <img
                src={actor.avatar}
                alt={`演员 ${actor.name}`}
                title={`年轻的母亲1 演员 ${actor.name}`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <dl className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">姓名</dt>
                  <dd className="font-medium text-foreground-900">{actor.name}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">饰演角色</dt>
                  <dd className="font-medium text-foreground-900">{actor.role}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">角色类型</dt>
                  <dd>
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                      {actor.roleType}
                    </span>
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">参演剧集</dt>
                  <dd className="font-medium text-foreground-900">{actorEpisodes.length} 集</dd>
                </div>
              </dl>
            </div>
          </div>

          <div>
            <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">
              {actor.name}
              <span className="ml-3 align-middle text-base font-normal text-foreground-500">
                饰 {actor.role}
              </span>
            </h1>

            <section className="mt-6">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                演员简介
              </h2>
              <p className="mt-3 text-sm leading-loose text-foreground-700">{actor.bio}</p>
            </section>

            <section className="mt-8">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                参演剧集
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {actorEpisodes.map((episode) => (
                  <Link
                    key={episode.id}
                    to={`/detail/episode/${episode.id}`}
                    className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                  >
                    <div className="relative w-full aspect-video overflow-hidden bg-background-200">
                      <img
                        src={episode.cover}
                        alt={`第${episode.ep}集 ${episode.title}`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute left-2 top-2 rounded-md bg-primary-950/80 px-2 py-0.5 text-[11px] text-background-50">
                        第 {episode.ep} 集
                      </span>
                    </div>
                    <div className="p-3">
                      <h3 className="line-clamp-1 text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                        {episode.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">{episode.airDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            <section className="mt-8">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                相关采访与剧情解析
              </h2>
              <div className="space-y-3">
                {relatedArticles.map((plot) => (
                  <Link
                    key={plot.id}
                    to={`/detail/plot/${plot.id}`}
                    className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                  >
                    <span className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img src={plot.thumbnail} alt={plot.title} className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-1 block text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                        {plot.title}
                      </span>
                      <span className="mt-1 block text-xs text-foreground-500">
                        {plot.date} · {plot.readTime}
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500" />
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}